import { SOCIAL_AD_TYPES } from "utils/constants";
import { MEGA } from "utils/common";

import { isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import { filesize, dimensions } from "widgets/forms/validators/Assets";
import {
	requiredAsset,
	errorMessageReducer,
	getCardWarnings
} from "./validators";

export function fieldCount(value, { min = 0, max = Infinity }){
	if(value?.length < min || value?.filter(Boolean).length < min) {
		return `At least ${min} required.`;
	}else if(value?.length > max) {
		return `Cannot have more than ${max}.`;
	}
}

function getFieldCountWarnings(fields, values, constraints) {
	const { MAX_INPUTS, MIN_INPUTS } = constraints;

	let errors = fields.reduce((fieldCountErrors, field) => {
		const max = MAX_INPUTS[field]
			, min = MIN_INPUTS[field]
			, value = values[field] || []
			, errorName = `${field}Count`
			, error = fieldCount(value, { min, max });

		if(error) {
			fieldCountErrors = {
				...fieldCountErrors,
				[errorName]: error,
			};
		}

		return fieldCountErrors;
	}, {});

	return errors;
}

export function getFieldArrayWarnings(validation, allValues) {
	let errors = {};
	let hasErrors = false;

	Object.entries(validation).map(([fieldArrayName, validators]) => {
		const values = allValues[fieldArrayName] || [];
		const fieldArrayErrors = values.reduce((prev, val, index) => {
			validators.forEach((validator) => {
				let message = validator(val, values);
				if(message) {
					hasErrors = true;
					if(!prev[index]){
						prev[index] = "";
					}
					prev[index] = `${index + 1} - ${message}`;
				}
			});

			return prev;
		}, {});

		if(hasErrors) {
			errors = {
				...errors,
				[fieldArrayName]: fieldArrayErrors,
			};
		}
	});

	return errors;
}

export const GOOGLE_DEMAND_GEN_IMAGE_CONSTRAINTS = {
	MAX_INPUTS: {
		images: 20,
		logos: 5,
		headlines: 5,
		descriptions: 5,
	},
	MIN_INPUTS: {
		images: 1,
		logos: 1,
		headlines: 1,
		descriptions: 1,
	},
	MAX_LENGTH_HEADLINES: 40,
	MAX_LENGTH_DESCRIPTIONS: 90,
	MAX_LENGTH_BUSINESS_NAME: 25,
	MAX_LENGTH_LANDING_PAGE: 800,
	IMAGE_ACCEPT: ".gif, .jpeg, .jpg, .png",
	MAX_IMAGE_MB: 5.12,
	LOGO_DIMENSIONS: { minWidth: 128, minHeight: 128 },
};

export function googleDemandGenImageWarning(allValues, props){
	const {
		MAX_INPUTS,
		MIN_INPUTS,
		MAX_LENGTH_HEADLINES,
		MAX_LENGTH_DESCRIPTIONS,
		MAX_LENGTH_BUSINESS_NAME,
		MAX_LENGTH_LANDING_PAGE,
		MAX_IMAGE_MB,
		LOGO_DIMENSIONS,
	} = GOOGLE_DEMAND_GEN_IMAGE_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE;
	const values = allValues[formName] || [];

	const warn = {
		businessName: [requiredNotNull, maxLength(MAX_LENGTH_BUSINESS_NAME)],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		mobileLandingPage: [isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
	};

	const warnings = errorMessageReducer(warn, values, formName);
	let errors = {
		...warnings?.[formName],
	};

	const fieldArrayWarn = {
		headlines: [maxLength(MAX_LENGTH_HEADLINES)],
		descriptions: [maxLength(MAX_LENGTH_DESCRIPTIONS)],
		images: [filesize({ max: MAX_IMAGE_MB * MEGA })],
		logos: [filesize({ max: MAX_IMAGE_MB * MEGA }), dimensions(LOGO_DIMENSIONS)],
	};

	const fieldCountWarn = [
		"headlines",
		"descriptions",
		"images",
		"logos",
	];

	const fieldArrayWarnings = getFieldArrayWarnings(fieldArrayWarn, values);
	const fieldCountWarnings = getFieldCountWarnings(fieldCountWarn, values, { MAX_INPUTS, MIN_INPUTS });

	if(Object.keys(fieldArrayWarnings).length) {
		errors = {
			...errors,
			...fieldArrayWarnings,
		};
	}

	if(Object.keys(fieldCountWarnings).length) {
		errors = {
			...errors,
			...fieldCountWarnings,
		};
	}

	if(Object.keys(errors).length){
		return {
			[formName]: {
				...errors,
			},
		};
	}
}

export function googleDemandGenImageValidator(allValues, props){
	const formName = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE;
	const values = allValues[formName] || {};

	const warn = {
		name: [requiredNotNull, creativeNameMaxLength],
	};

	return errorMessageReducer(warn, values, formName);
}

export const GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS = {
	MAX_IMAGE_MB: 5.12,
	IMAGE_ACCEPT: ".gif, .jpeg, .jpg, .png",
	MAX_LENGTH_HEADLINE: 40,
	MAX_LENGTH_DESCRIPTION: 90,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_LENGTH_BUSINESS_NAME: 25,
	LOGO_DIMENSIONS: {
		minWidth: 128,
		minHeight: 128,
	},
};

function requiredCardAspectRatios(value = {}, allValues){
	// Each card has to have an image for each requiredAspectRatios property with a truthy value.
	const selectedRatios = allValues.requiredAspectRatios || {};
	const requiredImages = Object.keys(selectedRatios).filter(key => selectedRatios[key]);

	const missing = requiredImages.filter(ratio => !value[ratio]);

	if(missing.length){
		return `${missing.join(", ")} image${(missing.length > 1) ? "s are" : " is"} required`;
	}
}

function googleRequiredAspectRatios(value = {}){
	if(!(value.square || value.horizontal)){
		return "A horizontal or square image is required";
	}
}

export function googleDemandGenCarouselWarning(allValues, props){
	const {
		MAX_IMAGE_MB,
		LOGO_DIMENSIONS,
		MAX_LENGTH_HEADLINE,
		MAX_LENGTH_DESCRIPTION,
		MAX_LENGTH_LANDING_PAGE,
		MAX_LENGTH_BUSINESS_NAME,
	} = GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL;
	const values = allValues[formName] || {};

	const warn = {
		headline: [requiredNotNull, maxLength(MAX_LENGTH_HEADLINE)],
		description: [requiredNotNull, maxLength(MAX_LENGTH_DESCRIPTION)],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		mobileLandingPage: [isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		businessName: [requiredNotNull, maxLength(MAX_LENGTH_BUSINESS_NAME)],
		logo: [requiredAsset, filesize({ max: MAX_IMAGE_MB * MEGA }), dimensions(LOGO_DIMENSIONS)],
		requiredAspectRatios: [googleRequiredAspectRatios],
	};

	const mainWarnings = errorMessageReducer(warn, values, formName);

	const allWarnings = {
		...mainWarnings?.[formName],
	};

	const { cards = []} = values;
	const cardValidators = {
		images: [requiredCardAspectRatios],
		headline: [requiredNotNull, maxLength(MAX_LENGTH_HEADLINE)],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		mobileLandingPage: [isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
	};

	const cardWarnings = getCardWarnings(cards, values, cardValidators);
	if(cardWarnings.length){
		allWarnings.cards = cardWarnings;
	}

	if(Object.keys(allWarnings).length){
		return { [formName]: allWarnings };
	}
}

export function googleDemandGenCarouselValidator(allValues, props){
	const formName = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}
